import { AxiosInstance } from "axios";
import {
  Board,
  BoardDetail,
  BoardSummary,
  BoardSettings,
  ConnectionPreview,
  CreateBoardRequest,
  UpdateBoardRequest,
} from "../types";

/**
 * Board API endpoints
 * Platform-agnostic - works with any HTTP client
 *
 * All endpoints are relative to the API base (e.g., /api/boards)
 * The baseURL should already include the region URL
 */
export class BoardsApi {
  constructor(private client: AxiosInstance) {}

  private parseConnectionConfiguration(
    connection: BoardDetail["connections"][number],
  ): void {
    if (
      connection.configurationJson &&
      typeof connection.configurationJson === "string"
    ) {
      try {
        connection.configuration = JSON.parse(connection.configurationJson);
      } catch (e) {
        console.error("Failed to parse connection configuration", e);
      }
    }
  }

  /**
   * Get all boards for the current user
   */
  async getBoards(): Promise<BoardSummary[]> {
    const response = await this.client.get<BoardSummary[]>("/api/boards");
    return response.data;
  }

  /**
   * Get a specific board by ID with full details
   */
  async getBoard(boardId: string, timeZoneIANA?: string): Promise<BoardDetail> {
    const tz = timeZoneIANA || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await this.client.get<BoardDetail>(
      `/api/boards/${boardId}?timeZoneIANA=${encodeURIComponent(tz)}`,
    );

    // Parse connection configurations from JSON if needed
    const board = response.data;
    if (board.connections) {
      board.connections.forEach((connection) => {
        this.parseConnectionConfiguration(connection);
      });
    }

    // Ensure configuration.tagColours exists
    if (!board.configuration) {
      board.configuration = {};
    }
    if (!board.configuration.tagColours) {
      board.configuration.tagColours = {};
    }

    return board;
  }

  /**
   * Create a new board (empty board)
   */
  async createBoard(
    request: CreateBoardRequest = {},
    hubConnectionId?: string,
  ): Promise<Board> {
    const url = hubConnectionId
      ? `/api/boards?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : "/api/boards";
    const response = await this.client.post<Board>(url, request);
    return response.data;
  }

  /**
   * Update board settings
   */
  async updateBoardSettings(
    boardId: string,
    settings: BoardSettings,
    hubConnectionId?: string,
  ): Promise<BoardSettings> {
    const url = hubConnectionId
      ? `/api/boards/${boardId}?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : `/api/boards/${boardId}`;
    const response = await this.client.put<BoardSettings>(url, settings);
    return response.data;
  }

  /**
   * Update an existing board (convenience method)
   */
  async updateBoard(
    boardId: string,
    request: UpdateBoardRequest,
    hubConnectionId?: string,
  ): Promise<Board> {
    const url = hubConnectionId
      ? `/api/boards/${boardId}?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : `/api/boards/${boardId}`;
    const response = await this.client.put<Board>(url, request);
    return response.data;
  }

  /**
   * Delete a board
   */
  async deleteBoard(boardId: string, hubConnectionId?: string): Promise<void> {
    const url = hubConnectionId
      ? `/api/boards/${boardId}?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : `/api/boards/${boardId}`;
    await this.client.delete(url);
  }

  /**
   * Archive a board
   */
  async archiveBoard(boardId: string): Promise<void> {
    await this.updateBoard(boardId, { isArchived: true });
  }

  /**
   * Unarchive a board
   */
  async unarchiveBoard(boardId: string): Promise<void> {
    await this.updateBoard(boardId, { isArchived: false });
  }

  /**
   * Sync connections for a board
   */
  async syncConnections(
    boardId: string,
    fullSync: boolean,
    hubConnectionId?: string,
  ): Promise<number> {
    const url = hubConnectionId
      ? `/api/boards/${boardId}/SyncConnections?hubConnectionId=${encodeURIComponent(hubConnectionId)}&fullSync=${fullSync}`
      : `/api/boards/${boardId}/SyncConnections?fullSync=${fullSync}`;
    const response = await this.client.put<number>(url, {});
    return response.data;
  }

  /**
   * Toggle mute notifications for a board
   */
  async toggleMuteNotifications(
    boardId: string,
    hubConnectionId?: string,
  ): Promise<boolean> {
    const url = hubConnectionId
      ? `/api/boards/${boardId}/ToggleMuteNotifications?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : `/api/boards/${boardId}/ToggleMuteNotifications`;
    const response = await this.client.put<boolean>(url, {});
    return response.data;
  }

  /**
   * Get a specific connection for a board
   */
  async getConnection(
    boardId: string,
    connectionId: string,
  ): Promise<BoardDetail["connections"][number]> {
    const response = await this.client.get<BoardDetail["connections"][number]>(
      `/api/boards/${boardId}/connections/${connectionId}`,
    );
    const connection = response.data;
    this.parseConnectionConfiguration(connection);
    return connection;
  }

  /**
   * Create a connection for a source on a board
   */
  async createConnection(
    boardId: string,
    sourceId: string,
    hubConnectionId?: string,
  ): Promise<BoardDetail["connections"][number]> {
    const url = hubConnectionId
      ? `/api/boards/${boardId}/connections?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : `/api/boards/${boardId}/connections`;

    const response = await this.client.post<BoardDetail["connections"][number]>(
      url,
      { sourceId, boardId },
    );
    const connection = response.data;
    this.parseConnectionConfiguration(connection);
    return connection;
  }

  /**
   * Update an existing connection on a board
   */
  async updateConnection(
    boardId: string,
    connectionId: string,
    connection: BoardDetail["connections"][number],
    hubConnectionId?: string,
  ): Promise<BoardDetail["connections"][number]> {
    const url = hubConnectionId
      ? `/api/boards/${boardId}/connections/${connectionId}?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : `/api/boards/${boardId}/connections/${connectionId}`;

    const payload: BoardDetail["connections"][number] = {
      ...connection,
      configurationJson:
        typeof connection.configurationJson === "string"
          ? connection.configurationJson
          : JSON.stringify(connection.configuration ?? {}),
    };

    const response = await this.client.put<BoardDetail["connections"][number]>(
      url,
      payload,
    );
    const updated = response.data;
    this.parseConnectionConfiguration(updated);
    return updated;
  }

  /**
   * Dry-run a connection's task fetch against an unsaved configuration.
   * Returns what the current editor settings would find, classified against the board's
   * existing tasks. Read-only — the posted configuration is never persisted.
   *
   * @param boardId Board the connection belongs to.
   * @param connectionId Connection to preview.
   * @param configuration Unsaved configuration object from the editor.
   * @param signal Optional abort signal, so a superseded preview can be cancelled.
   */
  async previewConnection(
    boardId: string,
    connectionId: string,
    configuration: unknown,
    signal?: AbortSignal,
  ): Promise<ConnectionPreview> {
    const url = `/api/boards/${encodeURIComponent(boardId)}/connections/${encodeURIComponent(connectionId)}/preview`;

    const response = await this.client.post<ConnectionPreview>(
      url,
      { configurationJson: JSON.stringify(configuration ?? {}) },
      { signal },
    );

    return response.data;
  }

  /**
   * Delete a connection from a board
   */
  async deleteConnection(
    boardId: string,
    connectionId: string,
    hubConnectionId?: string,
  ): Promise<void> {
    const url = hubConnectionId
      ? `/api/boards/${boardId}/connections/${connectionId}?hubConnectionId=${encodeURIComponent(hubConnectionId)}`
      : `/api/boards/${boardId}/connections/${connectionId}`;
    await this.client.delete(url);
  }

  /**
   * Start an OAuth flow for a new connection.
   * Returns the provider auth URL to open in a popup.
   * Additional setup values (e.g. org URL, subdomain) collected from the SPA dialog
   * are forwarded as the request body for sources that need them.
   */
  async oauthStart(
    boardId: string,
    protocol: "oauth2" | "oauth1",
    sourceId: string,
    sourceSetup: Record<string, string> = {},
  ): Promise<{ authUrl: string }> {
    if (protocol === "oauth1") {
      const response = await this.client.get<{ authUrl: string }>(
        `/api/boards/${boardId}/oauth1/start/${sourceId}`,
      );
      return response.data;
    }
    const response = await this.client.post<{ authUrl: string }>(
      `/api/boards/${boardId}/oauth2/start/${sourceId}`,
      sourceSetup,
    );
    return response.data;
  }

  /**
   * Start an OAuth re-authorisation flow for an existing connection.
   * Returns the provider auth URL to open in a popup.
   */
  async oauthReauthorize(
    boardId: string,
    protocol: "oauth2" | "oauth1",
    connectionId: string,
    sourceSetup: Record<string, string> = {},
  ): Promise<{ authUrl: string }> {
    if (protocol === "oauth1") {
      const response = await this.client.get<{ authUrl: string }>(
        `/api/boards/${boardId}/oauth1/reauthorize/${connectionId}`,
      );
      return response.data;
    }
    const response = await this.client.post<{ authUrl: string }>(
      `/api/boards/${boardId}/oauth2/reauthorize/${connectionId}`,
      sourceSetup,
    );
    return response.data;
  }

  /**
   * Connect to Azure DevOps using a Personal Access Token.
   * Creates the connection directly without an OAuth popup.
   */
  async vstsPatConnect(
    boardId: string,
    pat: string,
  ): Promise<{ profileName: string; profileId: string }> {
    const response = await this.client.post<{
      profileName: string;
      profileId: string;
    }>(`/api/boards/${boardId}/vsts/pat-connect`, { pat });
    return response.data;
  }
}
